import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AddCustomerModal from '../modals/AddCustomerModal';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Customer } from '../types';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '../constants/featureFlags';

const Customers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const navigate = useNavigate();
  const showDeltaPay = useFeatureFlag(FEATURE_FLAGS.DELTA_PAY);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);

  const fetchCustomers = async (search: string = '', page: number = 1) => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(`/customers?search=${encodeURIComponent(search)}&page=${page}`, {
      });
      const data = await res.json();
      setCustomers(data.customers ?? []);
      setTotalPages(data.total_pages);
      setCurrentPage(data.current_page);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initialPage = parseInt(searchParams.get('page') || '1', 10);
    const page = isNaN(initialPage) || initialPage < 1 ? 1 : initialPage;
    fetchCustomers('', page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  return (
    <div className="px-4 md:px-6 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Customers</h1>
        <button
          onClick={() => {
            setSelectedCustomer(null);
            setIsModalOpen(true);
          }}
          className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-auto"
        >
          Add Customer
        </button>
      </div>

      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 mb-4">
        <input
          type="text"
          placeholder="Search by First Name, Last Name, Cell Number, or Customer Code"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearchParams(prev => { prev.set('page', '1'); return prev; });
              fetchCustomers(searchTerm, 1);
            }
            }}
            className="border px-4 py-2 rounded-md w-full md:w-[36rem]"
          />
        <button
          onClick={() => {
            setSearchParams(prev => { prev.set('page', '1'); return prev; });
            fetchCustomers(searchTerm, 1);
          }}
          className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
        >
          Search
        </button>
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              setSearchParams(prev => { prev.set('page', '1'); return prev; });
              fetchCustomers('', 1);
            }}
            className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-semibold hover:bg-gray-400"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading && (
        <div className="w-full h-1 bg-gray-200 rounded overflow-hidden mb-2">
          <div className="h-full bg-blue-500 animate-pulse" style={{ width: '100%' }}></div>
        </div>
      )}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-md shadow-sm">
            <thead>
              <tr className="bg-gray-100 text-left text-sm font-medium text-gray-700">
                <th className="px-4 py-2">ID</th>
                <th className="hidden md:table-cell px-4 py-2">Created At</th>
                <th className="px-4 py-2">Full Name</th>
                <th className="px-4 py-2">Cellphone</th>
                <th className="hidden md:table-cell px-4 py-2">Email Address</th>
                <th className="hidden md:table-cell px-4 py-2">Verified</th>
                <th className="hidden md:table-cell px-4 py-2">WhatsApp</th>
                <th className="hidden md:table-cell px-4 py-2">Origin</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.id}
                  className={`border-t border-gray-100 text-sm text-gray-800 hover:bg-yellow-100 ${
                    openActionMenuId === c.id ? 'bg-yellow-100' : ''
                  }`}
                >
                  <td className="px-4 py-2">{c.code}</td>
                  <td className="hidden md:table-cell px-4 py-2">
                    {new Date(c.created_at).toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    })}
                  </td>
                  <td className="px-4 py-2">
                  <a
                    href={`/customers/${c.id}`}
                    className="text-blue-500 hover:underline"
                  >
                    {c.full_name}
                  </a>
                </td>
                  <td className="px-4 py-2">{c.cell_number}</td>
                  <td className="hidden md:table-cell px-4 py-2">{c.email}</td>
                  <td className="hidden md:table-cell px-4 py-2">
                    <div className="relative group inline-block">
                      <span
                        className={`inline-block w-3 h-3 rounded-full ${
                          c.phone_verified_at ? 'bg-green-500' : 'bg-red-500'
                        }`}
                      ></span>
                      <div className="absolute bottom-full mb-2 w-52 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                        {c.phone_verified_at
                          ? `Verified on ${new Date(c.phone_verified_at).toLocaleDateString()}`
                          : 'Not verified'}
                      </div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-4 py-2">
                    <div className="relative group inline-block">
                      <span
                        className={`inline-block w-3 h-3 rounded-full ${
                          c.deliverability_status === 'successful'
                            ? 'bg-green-500'
                            : c.deliverability_status === 'failed'
                            ? 'bg-red-500'
                            : 'bg-amber-400'
                        }`}
                      ></span>
                      <div className="absolute bottom-full mb-2 w-52 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                        {c.deliverability_status === 'successful'
                          ? 'Last WhatsApp message sent successfully'
                          : c.deliverability_status === 'failed'
                          ? 'Last WhatsApp message failed'
                          : 'No WhatsApp messages sent'}
                      </div>
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-4 py-2">{c.origin ? c.origin.replace(/_/g, ' ').toUpperCase() : '—'}</td>
                  <td className="px-4 py-2">
                    <div ref={actionMenuRef} className="relative inline-block text-left">
                      <button
                        onClick={() => setOpenActionMenuId(openActionMenuId === c.id ? null : c.id)}
                        className="p-1 rounded hover:bg-gray-100"
                      >
                        <span className="text-xl">⋯</span>
                      </button>
                      {openActionMenuId === c.id && (
                        <div className="absolute right-0 mt-1 w-28 bg-white border border-gray-200 rounded shadow-md z-10">
                          <button
                            onClick={() => {
                              navigate(`/customers/${c.id}`);
                              setOpenActionMenuId(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            View Customer
                          </button>
                          <button
                            onClick={() => {
                              handleEdit(c);
                              setOpenActionMenuId(null);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      <div className="flex justify-center items-center space-x-4 mt-4">
        <button
          disabled={currentPage === 1}
          onClick={() => {
            const newPage = currentPage - 1;
            setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
            fetchCustomers(searchTerm, newPage);
          }}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => {
            const newPage = currentPage + 1;
            setSearchParams(prev => { prev.set('page', String(newPage)); return prev; });
            fetchCustomers(searchTerm, newPage);
          }}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
      <AddCustomerModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCustomer(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          fetchCustomers();
          setSelectedCustomer(null);
        }}
        selectedCustomer={selectedCustomer}
        showDeltaPay={showDeltaPay}
      />
    </div>
  );
};

export default Customers;
